import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, CATEGORIES, ASSETS, PRACTICE } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import { localizeService, localizeCategory } from "@/i18n/data.es";
import SunDivider from "@/components/site/SunDivider";
import Seo from "@/components/site/Seo";
import { servicesListSchema, breadcrumbSchema } from "@/lib/seo";

export default function ServicesIndexPage() {
  const { t, lang } = useT();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <>
      <Seo
        title="Dental Services in Leesburg, VA"
        description="Comprehensive dental care in Leesburg, VA — cosmetic, restorative, implants, oral surgery, periodontics, sedation and family dentistry."
        path="/services"
        jsonLd={[
          servicesListSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
        ]}
      />
      <PageHero
        eyebrow={t("Dental Care · Leesburg, VA")}
        title={
          <>
            {t("Comprehensive care,")}{" "}
            <span className="italic text-[var(--accent)]">{t("under one warm roof")}</span>.
          </>
        }
        subtitle={`${t("Explore")} ${SERVICES.length} ${t("treatments organized into")} ${CATEGORIES.length} ${t("care categories — every service we offer to families across Loudoun.")}`}
      />

      <section
        className="px-6 md:px-10 lg:px-16 pb-16 md:pb-20"
        data-testid="services-index"
      >
        <nav
          aria-label="Category quick jump"
          data-testid="services-category-nav"
          className="flex flex-wrap items-center justify-center gap-2 mb-14"
        >
          {CATEGORIES.map((c) => {
            const lc = localizeCategory(c, lang);
            return (
              <a
                key={c.slug}
                href={`#cat-${c.slug}`}
                data-testid={`category-jump-${c.slug}`}
                className="text-[11px] uppercase tracking-[0.22em] px-4 py-2 rounded-full bg-[var(--surface)] border border-[var(--border)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)]"
              >
                {lc.title}
              </a>
            );
          })}
        </nav>

        {CATEGORIES.map((cat) => {
          const items = SERVICES.filter((s) => s.category === cat.slug);
          if (items.length === 0) return null;
          const lc = localizeCategory(cat, lang);
          return (
            <section
              key={cat.slug}
              id={`cat-${cat.slug}`}
              data-testid={`category-section-${cat.slug}`}
              className="scroll-mt-28 mb-16 md:mb-20"
            >
              <header className="mb-7 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                  <div className="font-mono-eyebrow text-[var(--accent)]">
                    {String(CATEGORIES.indexOf(cat) + 1).padStart(2, "0")} ·{" "}
                    {items.length} {items.length === 1 ? t("treatment") : t("treatments")}
                  </div>
                  <h2 className="font-serif-display text-[var(--primary)] mt-2 text-[36px] md:text-[48px] leading-tight tracking-tight">
                    {lc.title}
                  </h2>
                </div>
                <p className="text-[var(--text-muted)] max-w-md text-[15px]">
                  {lc.blurb}
                </p>
              </header>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((s, i) => {
                  const ls = localizeService(s, lang);
                  return (
                  <motion.div
                    key={s.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.45, delay: i * 0.03 }}
                  >
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`services-card-${s.slug}`}
                      className="group block bg-[var(--surface)] border border-[var(--border)] rounded-3xl p-7 h-full hover:bg-[var(--primary)] hover:text-[var(--bg)] hover:border-[var(--primary)] transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-full bg-[var(--primary)] grid place-content-center group-hover:bg-[var(--accent)]">
                          <img
                            src={ASSETS.serviceIcons[s.icon]}
                            alt=""
                            className="w-7 h-7 object-contain"
                            aria-hidden="true"
                          />
                        </div>
                        <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 -rotate-12 group-hover:rotate-0 transition-all" />
                      </div>
                      <h3 className="font-serif-display text-[24px] mt-5 leading-tight">
                        {ls.title}
                      </h3>
                      <p className="mt-2 text-sm opacity-80 group-hover:opacity-90">
                        {ls.short}
                      </p>
                    </Link>
                  </motion.div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <div className="mt-16 text-center max-w-2xl mx-auto">
          <SunDivider className="mb-4" />
          <p className="text-[var(--text-muted)]">
            {t("Don't see exactly what you're looking for? Give us a call at")}{" "}
            <a
              href={PRACTICE.phoneHref}
              className="text-[var(--primary)] underline underline-offset-4 hover:text-[var(--accent)]"
            >
              {PRACTICE.phone}
            </a>{" "}
            {t("and we'll point you in the right direction.")}
          </p>
        </div>
      </section>
    </>
  );
}

function PageHero({ eyebrow, title, subtitle }) {
  return (
    <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-10 md:pb-14 text-center">
      <div className="font-mono-eyebrow text-[var(--text-muted)]">
        {eyebrow}
      </div>
      <h1 className="font-serif-display text-[var(--primary)] mt-4 text-[48px] md:text-[80px] leading-[1] tracking-tight max-w-4xl mx-auto">
        {title}
      </h1>
      <SunDivider className="mt-6" />
      <p className="mt-5 max-w-xl mx-auto text-[var(--text-muted)] text-[17px]">
        {subtitle}
      </p>
    </section>
  );
}
